import React, { useCallback, useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ReferenceArea, ResponsiveContainer,
} from 'recharts';
import {
  ChartBox, GRID, AXIS, CHART_MARGIN, xAxisProps, yAxisProps, colorAt,
  KitTooltip, num,
} from '../../../../ChartUtility/recharts/_kit';
import {
  Mark, markFor, isZeroBased, axisDomain, compactTick,
  pivotSeries, openGaps, gapSpans, tickFormatterFor, labelFormatterFor,
  xAxisLabelFor, yAxisLabelFor,
} from '../../utils/meterSeries';
import '../../styles/MeterChart.css';

/** Grid, zero line and annotation weights follow the ONS chart build spec. */
const ZERO_LINE = '#B3B3B3';

/**
 * Days the meter reported nothing are washed red rather than left blank.
 *
 * A bar chart draws nothing at all for a period with no reading, so an outage
 * looked identical to empty space at the end of the axis — 32 - SPORTS CLUB
 * stopped reporting on 09 August and its chart simply ran out of bars. Grey
 * read as "not plotted"; red reads as "missing", which is what it is.
 */
const GAP_FILL = 'rgba(220, 53, 69, 0.12)';

/** The trend line's own colour, so it never takes a series' swatch. */
const TREND_LINE = '#6b7280';

/**
 * Recharts defaults `CartesianGrid` to a dashed stroke, which reads as a
 * projection and competes with genuine dashed reference lines. Passing an
 * empty string is the only way to clear it.
 */
const GRID_PROPS = { vertical: false, stroke: GRID, strokeDasharray: '' };

/** Curve for continuous series, matching the emissions trend on Scope 1. */
const CURVE = 'monotone';

/** Entry animation, at the duration the shared chart kit uses. */
const ANIMATION_MS = 700;

/**
 * Legend styling that sits under the plot rather than competing with it.
 *
 * `iconSize` is mirrored by MeterChart.css, which restores the key's size
 * against a global `svg { width: 100% }` rule in sidebar/common.css. That
 * rule travels with this component because both the details page and the
 * water dashboard draw it.
 */
const LEGEND = {
  iconType: 'plainline',
  iconSize: 14,
  wrapperStyle: { fontSize: 12, color: AXIS, paddingTop: 8, cursor: 'pointer' },
};

/**
 * Axis titles, set quieter than the plot and heavier than a tick so the eye
 * reads them as a caption for the scale rather than as another value on it.
 */
const AXIS_TITLE = { fontSize: 11, fontWeight: 600, fill: AXIS };

/**
 * Room the titles need.
 *
 * The y title is rotated and sits to the left of its ticks, so the axis has to
 * be wider than the ticks alone require; the x title sits below its ticks, so
 * the axis band has to be taller. Both are reserved rather than overlaid,
 * because an axis title drawn over a tick is worse than no title at all.
 */
const Y_AXIS_WIDTH = 78;
const X_AXIS_HEIGHT = 46;

/** Left gutter for the rotated y title, which `CHART_MARGIN` does not allow for. */
const LABELLED_MARGIN = { ...CHART_MARGIN, left: 6 };

/**
 * Builds the y-axis title.
 *
 * With two series on their own scales the unit is the same on both sides, so
 * repeating it says nothing about which axis belongs to which line. Each side
 * is named after its series instead, and tinted to match it — which is why an
 * override cannot apply there: one title cannot name two axes.
 *
 * The quantity is otherwise read from the graph type, which is right wherever
 * the type and the unit agree. A caller plotting something the type does not
 * imply — the overnight floor is a rate drawn as per-period bars — names it
 * itself rather than being labelled a volume in rate units.
 *
 * @param {object} series - The series the axis carries, when it carries one.
 * @param {string} graphType - The graph's type, for the quantity it measures.
 * @param {string} unit - Unit the values are in.
 * @param {string} override - Title the caller supplied, if any.
 * @returns {string} The axis title.
 */
const yTitle = (series, graphType, unit, override) => {
  if (!series) return override || yAxisLabelFor(graphType, unit);

  return unit ? `${series.name} (${unit})` : series.name;
};

/**
 * One chart on the meter details page, drawn to suit its quantity.
 *
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `sum_value`.
 * @param {string} graphType - netConsumption, massTotalizer or instantaneousFlow.
 * @param {string} stackByField - Field naming each series, when stacked.
 * @param {string} unit - Unit shown on the axis and in the tooltip.
 * @param {string} aggregation - Drives the x-axis tick density.
 * @param {Array<{y: number, label: string}>} thresholds - Annotated reference lines.
 * @param {boolean} dualAxis - Scale a second series against its own axis on the
 *   right, for series whose magnitudes are too far apart to share one.
 * @param {string} yAxisLabel - Y-axis title, for a caller whose quantity the
 *   graph type does not imply. Ignored on a split axis, which names each side
 *   after its series.
 * @param {string} seriesName - What to call the one series on an unstacked
 *   chart. The tooltip prints it, so the internal fallback shows through as a
 *   readout headed "value" unless the caller says otherwise.
 * @param {string} trendName - Name of the series to draw as a straight dashed
 *   line rather than in the chart's own mark, for a fitted trend.
 * @param {string} gapReason - Why the meter is silent right now, shown against
 *   a gap that is still open. A gap that closed again carries no reason: the
 *   meter's current state says nothing about what was wrong last Tuesday.
 * @param {number} height - Chart height in pixels.
 */
const MeterChart = ({
  rows = [],
  graphType,
  stackByField,
  unit = '',
  aggregation = 'day',
  thresholds = [],
  dualAxis = false,
  yAxisLabel,
  seriesName = 'value',
  gapReason = '',
  trendName = null,
  height = 320,
}) => {
  const mark = markFor(graphType);

  /**
   * Series the reader has switched off from the legend. Clicking a name
   * isolates the other, which is how the totaliser was read before: forward
   * flow dwarfs reverse on a shared axis, so hiding one is the only way to see
   * the other move.
   */
  const [hidden, setHidden] = useState({});

  const toggleSeries = useCallback((entry) => {
    const key = entry?.dataKey;
    if (!key) return;
    setHidden((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  const { data, series, gaps } = useMemo(() => {
    const pivoted = pivotSeries(rows, stackByField, seriesName);

    /* A fitted trend carries a value at every period, so counting it as a
       reading would mean no period ever looked unreported and the red bands
       would stop appearing the moment a trend was switched on. */
    const measured = pivoted.series.filter((s) => s.name !== trendName);
    const opened = openGaps(pivoted.data, measured);

    return {
      data: opened,
      series: pivoted.series,
      gaps: gapSpans(opened, measured),
    };
  }, [rows, stackByField, seriesName, trendName]);

  const shown = series.filter((s) => !hidden[s.key]);

  /** True only when a second series is present and still switched on. */
  const split = dualAxis && series.length === 2 && shown.length === 2;

  /* A hidden series must not hold the axis open, or isolating reverse flow
     would leave it flat against the floor of forward flow's scale. */
  const domainFor = useCallback(
    (keys) => axisDomain(data.flatMap((row) => keys.map((k) => row[k])), mark),
    [data, mark]
  );

  const leftDomain = useMemo(
    () => domainFor(split ? [series[0].key] : shown.map((s) => s.key)),
    [domainFor, split, series, shown]
  );

  const rightDomain = useMemo(
    () => (split ? domainFor([series[1].key]) : null),
    [domainFor, split, series]
  );

  /* The kit's tooltip prints the label it is handed, and Recharts hands it the
     raw x value — an ISO timestamp. Recharts only applies its own
     labelFormatter to the default tooltip, not to custom content, so the
     heading is formatted here before the kit renders it. */
  const renderTooltip = useCallback(
    (props) => {
      const heading = labelFormatterFor(aggregation)(props.label);

      if (props.active && !props.payload?.some((p) => p.value != null)) {
        const stillOut = gaps.some(
          (gap) => gap.open && props.label >= gap.from && props.label <= gap.to
        );

        return (
          <div className="im-gap-tip">
            <div className="im-gap-tip-hd">{heading}</div>
            <div className="im-gap-tip-body">
              {stillOut && gapReason ? gapReason : 'Nothing reported'}
            </div>
          </div>
        );
      }

      return (
        <KitTooltip
          {...props}
          label={heading}
          unit={unit}
          formatValue={(v) => (v == null ? 'No reading' : num(v))}
        />
      );
    },
    [aggregation, unit, gaps, gapReason]
  );

  if (!data.length || !series.length) {
    return (
      <ChartBox className="im-chart im-chart--empty" $height={height}>
        <span className="im-chart-empty">No readings in this window</span>
      </ChartBox>
    );
  }

  const zeroBased = isZeroBased(mark);

  return (
    <ChartBox className="im-chart" $height={height}>
      <ResponsiveContainer>
      <ComposedChart data={data} margin={LABELLED_MARGIN}>
        <CartesianGrid {...GRID_PROPS} />

        <XAxis
          dataKey="ts"
          {...xAxisProps}
          height={X_AXIS_HEIGHT}
          tickFormatter={tickFormatterFor(aggregation)}
          label={{
            value: xAxisLabelFor(aggregation),
            position: 'insideBottom',
            offset: 6,
            ...AXIS_TITLE,
          }}
        />

        {/* The kit's compactAxis only rounds above a thousand and prints a raw
            float below it, which on a meter reading ~490 gives a tick of
            127.30666666666665 that overflows the gutter. */}
        <YAxis
          {...yAxisProps}
          yAxisId="left"
          width={Y_AXIS_WIDTH}
          domain={leftDomain}
          tickCount={6}
          tickFormatter={compactTick}
          tick={split ? { fontSize: 12, fill: colorAt(0) } : yAxisProps.tick}
          label={{
            value: yTitle(split ? series[0] : null, graphType, unit, yAxisLabel),
            angle: -90,
            position: 'insideLeft',
            offset: 8,
            style: { textAnchor: 'middle' },
            ...AXIS_TITLE,
            ...(split ? { fill: colorAt(0) } : {}),
          }}
        />

        {/* Both series are in the same unit, so the axes are tinted to their
            lines: without that the eye compares heights across two different
            scales and reads reverse flow as the larger of the two. */}
        {split && (
          <YAxis
            {...yAxisProps}
            yAxisId="right"
            orientation="right"
            width={Y_AXIS_WIDTH}
            domain={rightDomain}
            tickCount={6}
            tickFormatter={compactTick}
            tick={{ fontSize: 12, fill: colorAt(1) }}
            label={{
              value: yTitle(series[1], graphType, unit),
              angle: 90,
              position: 'insideRight',
              offset: 8,
              style: { textAnchor: 'middle' },
              ...AXIS_TITLE,
              fill: colorAt(1),
            }}
          />
        )}

        {/* filterNull defaults to true, which drops a missing reading from the
            readout and makes a gap look like a series that is not plotted. */}
        <Tooltip
          shared
          filterNull={false}
          cursor={{ stroke: ZERO_LINE, strokeWidth: 1 }}
          content={renderTooltip}
        />

        {gaps.map((gap) => (
          <ReferenceArea
            key={`gap-${gap.from}`}
            x1={gap.from}
            x2={gap.to}
            fill={GAP_FILL}
            yAxisId="left"
            ifOverflow="hidden"
          />
        ))}

        {zeroBased && <ReferenceLine yAxisId="left" y={0} stroke={ZERO_LINE} strokeWidth={1.5} />}

        {thresholds.map((line) => (
          <ReferenceLine
            key={`thr-${line.y}`}
            yAxisId="left"
            y={line.y}
            stroke={ZERO_LINE}
            strokeWidth={2}
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
            label={{ value: line.label, position: 'insideTopRight', fontSize: 10, fill: '#64748b' }}
          />
        ))}

        {series.length > 1 && <Legend {...LEGEND} onClick={toggleSeries} />}

        {series.map((s, index) => {
          const colour = colorAt(index);

          /* A trend is a straight line whatever the other series are drawn as.
             Rendered as a bar it would read as another reading rather than as
             the shape of the ones beside it. */
          if (s.name === trendName) {
            return (
              <Line key={s.key} hide={hidden[s.key]} yAxisId="left" type="linear"
                dataKey={s.key} name={s.name} stroke={TREND_LINE}
                strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={false}
                isAnimationActive={false} connectNulls />
            );
          }

          if (mark === Mark.BARS) {
            return (
              <Bar key={s.key} hide={hidden[s.key]} yAxisId={split && index === 1 ? "right" : "left"} dataKey={s.key} name={s.name} fill={colour}
                maxBarSize={24} radius={[4, 4, 0, 0]}
                isAnimationActive animationDuration={ANIMATION_MS} />
            );
          }

          if (mark === Mark.LINE) {
            return (
              <Line key={s.key} hide={hidden[s.key]} yAxisId={split && index === 1 ? "right" : "left"} type={CURVE} dataKey={s.key} name={s.name}
                stroke={colour} strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: colour }} connectNulls={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                isAnimationActive animationDuration={ANIMATION_MS} />
            );
          }

          if (mark === Mark.STEP) {
            return (
              <Line key={s.key} hide={hidden[s.key]} yAxisId={split && index === 1 ? "right" : "left"} type="stepAfter" dataKey={s.key} name={s.name}
                stroke={colour} strokeWidth={2} dot={false} connectNulls={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
                isAnimationActive animationDuration={ANIMATION_MS} />
            );
          }

          return (
            <Area key={s.key} hide={hidden[s.key]} yAxisId={split && index === 1 ? "right" : "left"} type={CURVE} dataKey={s.key} name={s.name}
              stroke={colour} strokeWidth={2} fill={colour} fillOpacity={0.12}
              baseValue={0} dot={false} connectNulls={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
              isAnimationActive animationDuration={ANIMATION_MS} />
          );
        })}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartBox>
  );
};

export default MeterChart;
